/** @jsxImportSource @emotion/react */

import { FormEvent, Fragment, useCallback, useLayoutEffect, useState, useRef, ChangeEvent } from 'react';

import { AnswerCountState, data, Progress, TarotCard } from '../Data/index';
import { Fieldset } from '../Fieldset/fieldset';
import { Footer } from '../Footer/footer';
import { GlobalStyles } from '../GlobalStyles/index';
import { Header } from '../Header/header';
import { Main } from '../Main/main';
import { SubmitPanel } from '../SubmitPanel/submit-panel';
import { ProgressBar } from '../ProgressBar/progress-bar';
import { Result } from '../Result/result';
import { NewsletterPanel } from '../NewsletterPanel/newsletter-panel';
import { isPrerender } from '../../utilities/is-prerender';

export type ProgressState =
  | {
      status: Progress.Initial;
      currentQuestion: number;
      completion: boolean[];
    }
  | {
      status: Progress.InProgress;
      currentQuestion: number;
      completion: boolean[];
    }
  | {
      status: Progress.Invalid;
      currentQuestion: number;
      completion: boolean[];
    }
  | {
      status: Progress.Valid;
      currentQuestion: number;
      completion: boolean[];
    }
  | {
      status: Progress.Complete;
      currentQuestion: number;
      completion: boolean[];
      resultDetails?: TarotCard;
    };

const initialState: ProgressState = {
  status: Progress.Initial,
  currentQuestion: 0,
  completion: data.questions.map(() => false)
};

export const App = () => {
  // State
  const [progress, setProgress] = useState<ProgressState>(initialState);

  // Refs
  const formRef = useRef<HTMLFormElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const submitPanelRef = useRef<HTMLDivElement>(null);
  const currentVisibleRef = useRef<HTMLFieldSetElement | HTMLDivElement | null>(null);

  /**
   * When the quiz moves between states, check if we should scroll to areas of
   * the page.
   */
  useLayoutEffect(() => {
    if (progress.status === Progress.Initial) {
      scrollTo(headerRef.current, { behavior: 'auto' });
    }

    if (progress.status === Progress.Valid) {
      scrollTo(submitPanelRef.current);
    }

    if (progress.status === Progress.Complete) {
      scrollTo(resultRef.current);
    }
  }, [progress.status]);

  const initiateQuiz = () => {
    scrollTo(formRef.current);
  };

  const scrollTo = (
    element?: Element | null,
    options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'start', inline: 'nearest' }
  ) => {
    if (element) {
      element.scrollIntoView(options);
    }
  };

  const scrollToPreviousQuestion = () => {
    scrollTo(currentVisibleRef.current?.previousElementSibling);
  };

  const scrollToNextQuestion = () => {
    if (progress.status === Progress.Invalid) {
      // QUESTION COMPLETION IS DELAYED BY 1? (Console log is delayed, state is not)
      scrollToNextUnanswered();

      return;
      // BUT IF NO UNANSWERED, SCROLL TO SUBMITPANELREF
    }

    scrollTo(currentVisibleRef.current?.nextElementSibling);
  };

  const handleVisibility = useCallback((ref: HTMLFieldSetElement | HTMLDivElement) => {
    currentVisibleRef.current = ref;

    const count = Array.from(ref.parentElement!.children).indexOf(ref);

    // When we've reached the Submit Panel, the question count will still increase by 1 (to 8), to help us toggle buttons visible/hidden in the progress bar.
    setTimeout(() => {
      setProgress((prevState) => ({
        ...prevState,
        currentQuestion: count + 1
      }));
    }, 300);
  }, []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const form = event.currentTarget.form;

    if (!form) {
      return;
    }

    // Count how many questions have been answered.
    let count = 0;
    const completion = data.questions.map(() => false);

    for (let key in form.elements) {
      const element = form.elements[key];

      if (!(element instanceof RadioNodeList)) {
        continue;
      }

      if (element.value) {
        completion[count] = true;
      }

      count++;
    }

    setProgress((prevState) => ({
      // If state was previously INVALID
      // we are in a "checking unanswered questions" state
      // (We could add another state, "checking", but it's essentially the same as invalid)
      // So therefore should stay invalid and only scroll to the next unanswered (not just to the next Q)
      status: form.checkValidity() ? Progress.Valid : prevState.status === Progress.Invalid ? Progress.Invalid : Progress.InProgress,
      currentQuestion: prevState.currentQuestion,
      completion
    }));

    scrollToNextQuestion();
  };

  const scrollToNextUnanswered = () => {
    const form = formRef.current;

    if (!form) {
      return;
    }

    const fieldset = form.querySelector('fieldset:invalid');

    scrollTo(fieldset);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    if (!form.checkValidity()) {
      setProgress((prevState) => ({
        ...prevState,
        status: Progress.Invalid,
        currentQuestion: prevState.currentQuestion
      }));

      scrollToNextUnanswered();

      return;
    }

    let count: { [k: string]: number } = {};

    const elements = Array.from(form.querySelectorAll<HTMLInputElement>('input:checked'));

    for (let input of elements) {
      // Calculate the weight of the question.
      const weight = parseInt(input.getAttribute('data-value-weight') || '8', 10);
      const extra = 1 + Math.pow(10, -1 * weight);

      const value = input.value;
      count[value] = count[value] ? count[value] + extra : extra;
    }

    console.info(`Count:`, count);

    const result = getResult(count);
    const resultDetails = data.cards.find((c) => c.slug.toLowerCase() === result.toLowerCase());

    setProgress((prevState) => ({
      ...prevState,
      status: Progress.Complete,
      resultDetails
    }));
  };

  const handleReset = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    event.currentTarget.reset();

    setProgress(initialState);
  };

  const getResult = (selections: AnswerCountState) => {
    return Object.keys(selections).reduce((a, b) => (selections[a] > selections[b] ? a : b));
  };

  return (
    <Fragment>
      <GlobalStyles />
      <Header initiateQuiz={initiateQuiz} ref={headerRef} />
      <Main progress={progress}>
        <form noValidate onReset={handleReset} onSubmit={handleSubmit} ref={formRef}>
          {!isPrerender() &&
            data.questions.map((qaSet, i) => (
              <Fieldset
                key={`fieldset-${i}-key`}
                isVisible={progress.currentQuestion - 1 === i}
                index={i}
                qaSet={qaSet}
                onInputChange={handleChange}
                onVisible={handleVisibility}
              />
            ))}

          {progress.status !== Progress.Complete && <SubmitPanel onVisible={handleVisibility} progress={progress} ref={submitPanelRef} />}

          <ProgressBar onNextButtonClick={scrollToNextQuestion} onPreviousButtonClick={scrollToPreviousQuestion} progress={progress} />
        </form>

        {progress.status === Progress.Complete && progress.resultDetails && (
          <Result resultDetails={progress.resultDetails} ref={resultRef} />
        )}
      </Main>

      {!isPrerender() && (
        <Fragment>
          <NewsletterPanel />
          <Footer />
        </Fragment>
      )}
    </Fragment>
  );
};

App.displayName = 'App';
